import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useMemo } from 'react';
import {
  META_BLUE,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  WHATSAPP_GREEN,
} from 'styles/tokens';

import type { MetaAccountOption } from '../filters/useCampaignDynamicOptions';

export interface AdAccountSidebarProps {
  accounts: MetaAccountOption[];
  whatsappAvailable: boolean;
  selectedAccountIds: string[];
  whatsappSelected: boolean;
  isLoading?: boolean;
  onSelectionChange: (accountIds: string[], whatsappSelected: boolean) => void;
}

const titleSx = {
  color: alpha(NAVY, 0.55),
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  fontSize: '0.68rem',
  fontWeight: 700,
  fontFamily: 'Jost, sans-serif',
};

const entryLabelSx = {
  color: NAVY,
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: '0.85rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const entryMetaSx = {
  color: alpha(NAVY, 0.5),
  fontFamily: 'Jost, sans-serif',
  fontWeight: 500,
  fontSize: '0.68rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

const quickActionSx = {
  color: alpha(NAVY, 0.7),
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: '0.72rem',
  cursor: 'pointer',
  textTransform: 'none' as const,
  '&:hover': { color: NAVY },
};

const AdAccountSidebar: React.FC<AdAccountSidebarProps> = ({
  accounts,
  whatsappAvailable,
  selectedAccountIds,
  whatsappSelected,
  isLoading,
  onSelectionChange,
}) => {
  const selectedSet = useMemo(() => new Set(selectedAccountIds), [selectedAccountIds]);
  const totalCount = accounts.length + (whatsappAvailable ? 1 : 0);
  const selectedCount = selectedAccountIds.length + (whatsappSelected ? 1 : 0);

  const toggleAccount = (id: string) => {
    const next = selectedSet.has(id) ? selectedAccountIds.filter((x) => x !== id) : [...selectedAccountIds, id];
    onSelectionChange(next, whatsappSelected);
  };

  const toggleWhatsApp = () => {
    onSelectionChange(selectedAccountIds, !whatsappSelected);
  };

  const selectAll = () => {
    onSelectionChange(
      accounts.map((a) => a.id),
      whatsappAvailable
    );
  };

  const selectNone = () => {
    onSelectionChange([], false);
  };

  return (
    <Box
      sx={{
        borderRadius: RADIUS_CARD,
        bgcolor: SURFACE_CONTAINER_LOWEST,
        boxShadow: SHADOW,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 480, md: 'calc(100vh - 160px)' },
        minHeight: 320,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 1,
          gap: 1,
        }}
      >
        <Typography sx={titleSx}>Cuentas</Typography>
        <Box sx={{ display: 'flex', gap: 1.25 }}>
          <Typography component="span" sx={quickActionSx} onClick={selectAll}>
            Todas
          </Typography>
          <Typography component="span" sx={{ ...quickActionSx, color: alpha(NAVY, 0.4) }}>
            ·
          </Typography>
          <Typography component="span" sx={quickActionSx} onClick={selectNone}>
            Ninguna
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
        {isLoading && accounts.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={18} sx={{ color: NAVY }} />
          </Box>
        )}

        {accounts.length > 0 && (
          <>
            <GroupLabel color={META_BLUE} label="Meta Ads" />
            {accounts.map((account) => (
              <AccountEntry
                key={account.id}
                channel="meta"
                primaryLabel={account.name}
                secondaryLabel={account.currency ?? 'Meta'}
                selected={selectedSet.has(account.id)}
                onToggle={() => toggleAccount(account.id)}
              />
            ))}
          </>
        )}

        {whatsappAvailable && (
          <>
            <GroupLabel color={WHATSAPP_GREEN} label="WhatsApp" />
            <AccountEntry
              channel="whatsapp"
              primaryLabel="WhatsApp"
              secondaryLabel="Outbound"
              selected={whatsappSelected}
              onToggle={toggleWhatsApp}
            />
          </>
        )}

        {!isLoading && accounts.length === 0 && !whatsappAvailable && (
          <Typography
            sx={{
              color: alpha(NAVY, 0.5),
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.82rem',
              px: 1.5,
              py: 2,
              textAlign: 'center',
            }}
          >
            No hay cuentas disponibles.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: SURFACE_CONTAINER_LOW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ ...entryMetaSx, color: alpha(NAVY, 0.65) }}>Seleccionadas</Typography>
        <Typography
          sx={{
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            fontSize: '0.82rem',
          }}
        >
          {selectedCount}/{totalCount}
        </Typography>
      </Box>
    </Box>
  );
};

interface GroupLabelProps {
  label: string;
  color: string;
}

/** Divider between channel groups — a colored dot + uppercase label. Subtle marker so
 *  the user can tell WhatsApp and Meta accounts apart without extra chrome. */
const GroupLabel: React.FC<GroupLabelProps> = ({ label, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.25,
      pt: 1.25,
      pb: 0.5,
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: color,
      }}
    />
    <Typography
      sx={{
        color: alpha(NAVY, 0.55),
        fontFamily: 'Jost, sans-serif',
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}
    >
      {label}
    </Typography>
  </Box>
);

interface AccountEntryProps {
  channel: 'whatsapp' | 'meta';
  primaryLabel: string;
  secondaryLabel: string;
  selected: boolean;
  onToggle: () => void;
}

const AccountEntry: React.FC<AccountEntryProps> = ({ channel, primaryLabel, secondaryLabel, selected, onToggle }) => {
  const channelColor = channel === 'whatsapp' ? WHATSAPP_GREEN : META_BLUE;
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.25,
        py: 1,
        mb: 0.5,
        borderRadius: RADIUS_ITEM,
        cursor: 'pointer',
        bgcolor: selected ? alpha(channelColor, 0.08) : 'transparent',
        transition: 'background-color 120ms ease',
        '&:hover': {
          bgcolor: selected ? alpha(channelColor, 0.12) : alpha(NAVY, 0.04),
        },
      }}
    >
      <Checkbox
        checked={selected}
        size="small"
        disableRipple
        icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: '1.1rem', color: alpha(NAVY, 0.45) }} />}
        checkedIcon={<CheckBoxIcon sx={{ fontSize: '1.1rem', color: channelColor }} />}
        sx={{ p: 0 }}
      />
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: alpha(channelColor, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {channel === 'whatsapp' ? (
          <WhatsAppIcon sx={{ fontSize: 16, color: channelColor }} />
        ) : (
          <FacebookIcon sx={{ fontSize: 16, color: channelColor }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={entryLabelSx}>{primaryLabel}</Typography>
        <Typography sx={entryMetaSx}>{secondaryLabel}</Typography>
      </Box>
    </Box>
  );
};

export default AdAccountSidebar;
